/**
 * Episode of Care page — comprehensive management view for an Episode of Care.
 *
 * Episode management: group members, guarantor, primary patient changes, and change history.
 * Treatment plans for this episode are listed from CarePlan history, one row per version.
 */

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  Radio,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangleFilled,
  IconArrowLeft,
  IconClipboardList,
  IconCreditCard,
  IconHistory,
  IconShield,
  IconUserMinus,
  IconUserPlus,
  IconTransfer,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import type { Coverage } from '@medplum/fhirtypes';
import { getPrimaryActiveCoverage } from '@unify/fhir-model/resources/coverage';
import { useForm } from '@mantine/form';
import { useMedplum } from '@medplum/react';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEpisodeTreatmentPlanVersions } from '../../hooks/useEpisodeTreatmentPlanVersions';
import { CardOnFileSection } from '../../components/billing/CardOnFileSection';
import { usePatientCoverageAndPayment } from '../../hooks/usePatientCoverageAndPayment';
import type { SavedPaymentMethod } from '../../lib/stripeBillingService';
import { RecordPageHeader } from '../../components/RecordPageHeader';
import { useClients } from '../../contexts/ClientsContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { getClientDisplayName, getClientLegalName } from '../caseload/data';
import { buildClientSelectOptions } from '../../lib/clientSelectOptions';
import { ClientSelectOptionLabel } from '../../components/ClientSelectOptionLabel';
import { TransferModal, type TransferModalContext } from '../caseload/components';
import { mockDischargeSummaries } from './data';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { useEpisodeScopedClientChartBundle } from '../../hooks/useEpisodeScopedClientChartBundle';
import { useEpisodeDiagnosisDisplay } from '../../hooks/useEpisodeDiagnosisDisplay';
import {
  addGuarantorContactToEpisode,
  assignGuarantorExistingToEpisode,
  addNewClientToEpisode,
  addExistingClientToEpisode,
  removeParticipantFromEpisode,
  setEpisodeGuarantor,
} from '../../lib/episodePartyService';
import { canViewEpisodeOfCarePage } from '../../lib/rbac/eocChartScope';
import { useSupervisedPractitionerIds } from '../../hooks/useSupervisedPractitionerIds';
import { therapyServiceTypeLabel } from '../../lib/fhirValueSets/sessionAndEpisodeTypes';
import { getViewScope } from '../../lib/rbac/scopeAccess';
import { getStatusColor, formatStatusText } from '../../lib/statusUtils';
import type {
  EpisodeParty,
  PrimaryPatientSwitch,
} from './data';
import {
  addEpisodeGuarantorFormSchema,
  addEpisodeMemberFormSchema,
  episodePartyRelationshipForBot,
  episodePartyRelationshipSelectData,
  getDefaultAddEpisodeGuarantorFormValues,
  getDefaultAddEpisodeMemberFormValues,
  type AddEpisodeGuarantorFormValues,
  type AddEpisodeMemberFormValues,
} from '@/schemas/episode-party.schema';

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US');
  } catch {
    return dateString;
  }
}

function formatLocalDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function episodeStateStorageKey(clientId: string, episodeId: string): string {
  return `unify.medplum-ui.episodeState.${clientId}.${episodeId}`;
}

const DISCHARGE_REASONS = [
  { value: 'Treatment goals met', label: 'Treatment goals met' },
  { value: 'Client request', label: 'Client request' },
  { value: 'Transferred to another provider', label: 'Transferred to another provider' },
  { value: 'Non-compliance', label: 'Non-compliance / no-show' },
  { value: 'Moved out of area', label: 'Moved out of area' },
  { value: 'Insurance change', label: 'Insurance change' },
  { value: 'Left group therapy', label: 'Left group / voluntary withdrawal' },
  { value: 'Other', label: 'Other' },
];

const ADD_GUARANTOR_KEY = '__add_guarantor__';

interface EpisodeStateOverrides {
  primaryPatientHistory?: PrimaryPatientSwitch[];
}

function resolveGuarantorPatientId(
  guarantorKey: string | null,
  members: EpisodeParty[],
  localGuarantor?: EpisodeParty,
): string | undefined {
  if (!guarantorKey || guarantorKey === ADD_GUARANTOR_KEY) return undefined;
  if (guarantorKey.startsWith('ext-')) return undefined;
  if (members.some((m) => m.id === guarantorKey)) return guarantorKey;
  if (localGuarantor?.id === guarantorKey) return guarantorKey;
  return undefined;
}

function primaryCoverage(coverages: Coverage[]): Coverage | undefined {
  return getPrimaryActiveCoverage(coverages);
}

function coverageDisplayFields(cov: Coverage | undefined): {
  payer: string;
  memberId: string;
  groupId: string;
} | null {
  if (!cov) return null;
  return {
    payer: cov.payor?.[0]?.display ?? 'Unknown payer',
    memberId:
      cov.identifier?.find((id) => id.type?.coding?.[0]?.code === 'MB')?.value ??
      cov.subscriberId ??
      '',
    groupId: cov.class?.find((c) => c.type?.coding?.[0]?.code === 'group')?.value ?? '',
  };
}

function hasCardOnFile(paymentMethods: SavedPaymentMethod[]): boolean {
  return paymentMethods.length > 0;
}

function hasInsuranceOnFile(coverages: Coverage[]): boolean {
  return coverages.some((c) => c.status === 'active' || c.status === 'draft');
}

function getEpisodeTypeLabel(episodeType?: string): string | undefined {
  if (!episodeType) return undefined;
  return therapyServiceTypeLabel(episodeType);
}

function isMultiMemberEpisodeType(episodeType?: string): boolean {
  return episodeType === 'couples' || episodeType === 'family' || episodeType === 'group';
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function EpisodeOfCarePage(): JSX.Element {
  const { id: clientId, episodeId } = useParams<{ id: string; episodeId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { getClientById, clients: contextClients } = useClients();
  const { role, permissions } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const { ids: supervisedPractitionerIds, loading: superviseesLoading } = useSupervisedPractitionerIds();
  const viewScope = useMemo(() => getViewScope(role), [role]);
  const waitForPractitioner = (viewScope === 'OWN' && practitionerLoading) || superviseesLoading;

  const { bundle: medplumChart, loading: medplumChartLoading, refetch: refetchChart } = useEpisodeScopedClientChartBundle(
    clientId ?? undefined,
  );

  const client = useMemo(() => {
    if (!clientId) return null;
    return getClientById(clientId) ?? null;
  }, [clientId, getClientById]);

  const allClients = contextClients;

  const baseEpisode = useMemo(() => {
    if (!clientId || !episodeId) return null;
    return medplumChart?.episodes?.find((e) => e.id === episodeId) ?? null;
  }, [clientId, episodeId, medplumChart]);

  const { diagnosis: episodeDiagnosis, loading: episodeDiagnosisLoading } = useEpisodeDiagnosisDisplay(episodeId);
  const { rows: treatmentPlanVersions, loading: treatmentPlanVersionsLoading } = useEpisodeTreatmentPlanVersions(
    clientId,
    episodeId,
  );

  const canViewThisEpisode = useMemo(() => {
    if (!baseEpisode) {
      return true;
    }
    return canViewEpisodeOfCarePage(baseEpisode, {
      role,
      practitionerId: practitioner?.id,
      viewScope,
      supervisedPractitionerIds,
    });
  }, [baseEpisode, role, practitioner?.id, viewScope, supervisedPractitionerIds]);

  const dischargeSummary = useMemo(
    () => (episodeId ? mockDischargeSummaries.find((d) => d.episodeId === episodeId) : undefined),
    [episodeId],
  );

  const isMultiMemberEpisode = isMultiMemberEpisodeType(baseEpisode?.episodeType);
  const episodeTypeLabel = getEpisodeTypeLabel(baseEpisode?.episodeType);
  const canProvisionPortalUsers = permissions.edit_users === true;
  const episodePrimaryPatientId = baseEpisode?.primaryClinicalPatient?.id ?? clientId ?? '';

  // ── Episode management state ────────────────────────────────────────
  const [members, setMembers] = useState<EpisodeParty[]>([]);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [localGuarantor, setLocalGuarantor] = useState<EpisodeParty | undefined>();
  const [localHistory, setLocalHistory] = useState<PrimaryPatientSwitch[]>([]);

  // Guarantor select state
  const [guarantorKey, setGuarantorKey] = useState<string | null>(null);

  // Add contact state (guarantor who is not an existing member)
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [addingGuarantor, setAddingGuarantor] = useState(false);

  // Primary patient change state
  const [switchReason, setSwitchReason] = useState('');

  // Add member / guarantor forms
  const [addingMember, setAddingMember] = useState(false);
  const [partySaving, setPartySaving] = useState(false);

  const memberFormResolver = useMemo(() => zod4Resolver(addEpisodeMemberFormSchema), []);
  const memberForm = useForm<AddEpisodeMemberFormValues>({
    initialValues: getDefaultAddEpisodeMemberFormValues(),
    validate: memberFormResolver,
  });

  const guarantorAddFormResolver = useMemo(() => zod4Resolver(addEpisodeGuarantorFormSchema), []);
  const guarantorAddForm = useForm<AddEpisodeGuarantorFormValues>({
    initialValues: getDefaultAddEpisodeGuarantorFormValues(),
    validate: guarantorAddFormResolver,
  });

  const episodePartyRelationshipOptions = useMemo(() => episodePartyRelationshipSelectData(), []);

  // Member removal state
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeCategory, setDischargeCategory] = useState<string | null>(null);

  // ── Derived values ──────────────────────────────────────────────────
  const initialPrimaryId = isMultiMemberEpisode
    ? (baseEpisode?.primaryClinicalPatient?.id ?? null)
    : (client?.id ?? null);

  const clinicalPatientChanging = isMultiMemberEpisode && primaryId !== initialPrimaryId;

  const isPrimaryChart = baseEpisode?.primaryClinicalPatient?.id === client?.id;

  const primaryClientForGuarantorLink = baseEpisode?.primaryClinicalPatient?.id ?? client?.id;

  const billingPatientId = useMemo(() => {
    if (addingGuarantor) {
      return guarantorAddForm.values.mode === 'existing'
        ? guarantorAddForm.values.guarantorPatientId ?? undefined
        : undefined;
    }
    return resolveGuarantorPatientId(guarantorKey, members, localGuarantor);
  }, [
    addingGuarantor,
    guarantorAddForm.values.mode,
    guarantorAddForm.values.guarantorPatientId,
    guarantorKey,
    members,
    localGuarantor,
  ]);

  const {
    coverages: guarantorCoverages,
    paymentMethods: guarantorPaymentMethods,
    loading: guarantorBillingLoading,
  } = usePatientCoverageAndPayment(billingPatientId);

  const guarantorInsuranceDisplay = useMemo(
    () => coverageDisplayFields(primaryCoverage(guarantorCoverages)),
    [guarantorCoverages],
  );

  const requiresCardOnFile = Boolean(
    billingPatientId && (!addingGuarantor || guarantorAddForm.values.mode === 'existing'),
  );
  const guarantorHasCard = hasCardOnFile(guarantorPaymentMethods);
  const guarantorHasInsurance = hasInsuranceOnFile(guarantorCoverages);

  useEffect(() => {
    if (!baseEpisode || !clientId || !episodeId || !client) return;

    const defaultMembers = isMultiMemberEpisode
      ? (baseEpisode.groupMembers?.length
          ? baseEpisode.groupMembers
          : baseEpisode.primaryClinicalPatient
            ? [baseEpisode.primaryClinicalPatient]
            : [{ id: client.id, name: getClientDisplayName(client), relationship: 'Self' as const }])
      : [{ id: client.id, name: getClientDisplayName(client), relationship: 'Self' as const }];

    let overrides: EpisodeStateOverrides = {};
    try {
      const raw = localStorage.getItem(episodeStateStorageKey(clientId, episodeId));
      if (raw) overrides = JSON.parse(raw) as EpisodeStateOverrides;
    } catch { /* use defaults */ }

    setMembers(defaultMembers);
    setPrimaryId(
      isMultiMemberEpisode
        ? (baseEpisode.primaryClinicalPatient?.id ?? defaultMembers[0]?.id ?? null)
        : client.id,
    );
    setLocalGuarantor(baseEpisode.guarantor);
    setLocalHistory(overrides.primaryPatientHistory ?? baseEpisode.primaryPatientHistory ?? []);

    const resolvedGuarantor = baseEpisode.guarantor;
    if (resolvedGuarantor) {
      setGuarantorKey(resolvedGuarantor.id);
    } else {
      setGuarantorKey(defaultMembers[0]?.id ?? null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, episodeId, baseEpisode?.id, baseEpisode?.guarantor?.id, baseEpisode?.groupMembers?.length]);

  // ── Save episode management state ───────────────────────────────────
  const saveEpisodeState = useCallback((patch?: Partial<EpisodeStateOverrides>) => {
    if (!clientId || !episodeId) return;
    const nextHistory = patch?.primaryPatientHistory ?? localHistory;
    const overrides: EpisodeStateOverrides = {
      primaryPatientHistory: nextHistory,
    };
    localStorage.setItem(episodeStateStorageKey(clientId, episodeId), JSON.stringify(overrides));
  }, [clientId, episodeId, localHistory]);

  // ── Primary patient change confirmation ─────────────────────────────
  const handleConfirmPrimaryChange = useCallback(() => {
    if (!primaryId || !clinicalPatientChanging) return;

    const prevPrimary = baseEpisode?.primaryClinicalPatient;
    const newPrimaryMember = members.find((m) => m.id === primaryId);
    if (!prevPrimary || !newPrimaryMember) return;

    const switchEntry: PrimaryPatientSwitch = {
      changedAt: new Date().toISOString(),
      changedBy: 'Current User',
      previousPatient: prevPrimary,
      newPatient: newPrimaryMember,
      reason: switchReason || 'Not specified',
      treatmentPlanUpdated: true,
    };

    const nextHistory = [...localHistory, switchEntry];
    setLocalHistory(nextHistory);
    setSwitchReason('');
    saveEpisodeState({ primaryPatientHistory: nextHistory });

    notifications.show({
      title: 'Primary patient changed',
      message: `${prevPrimary.name} → ${newPrimaryMember.name}. Update the treatment plan on the client chart if needed.`,
      color: 'green',
    });
  }, [primaryId, clinicalPatientChanging, baseEpisode, members, switchReason, localHistory, saveEpisodeState]);

  const resetNewMemberForm = () => {
    setAddingMember(false);
    memberForm.reset();
  };

  // ── Guarantor save ──────────────────────────────────────────────────
  const handleSaveGuarantor = useCallback(async () => {
    if (!clientId || !episodeId || !baseEpisode || partySaving) return;

    const skipCardValidation = addingGuarantor && guarantorAddForm.values.mode === 'new';
    if (requiresCardOnFile && !skipCardValidation) {
      if (guarantorBillingLoading) {
        notifications.show({
          title: 'Loading billing details',
          message: 'Wait for the guarantor billing profile to finish loading.',
          color: 'yellow',
        });
        return;
      }
      if (!billingPatientId) {
        notifications.show({
          title: 'No billing profile',
          message: 'Select an episode member with a client billing profile, or add a guarantor with an existing account.',
          color: 'red',
        });
        return;
      }
      if (!guarantorHasCard) {
        notifications.show({
          title: 'Card required',
          message: 'The selected guarantor must have a card on file before billing can be updated for this episode.',
          color: 'red',
        });
        return;
      }
      if (!guarantorHasInsurance) {
        notifications.show({
          title: 'No insurance on file',
          message: 'This guarantor has no insurance on file. You can continue, but verify coverage before billing sessions.',
          color: 'yellow',
        });
      }
    }

    if (addingGuarantor) {
      const validation = guarantorAddForm.validate();
      if (validation.hasErrors) return;
    }

    setPartySaving(true);
    try {
      if (addingGuarantor) {
        const values = addEpisodeGuarantorFormSchema.parse(guarantorAddForm.values);
        const relationship = episodePartyRelationshipForBot(values.relationship);

        if (values.mode === 'existing') {
          if (!canProvisionPortalUsers) {
            notifications.show({
              title: 'Permission required',
              message: 'Assigning guarantor portal access requires Manage users permission.',
              color: 'red',
            });
            return;
          }
          await assignGuarantorExistingToEpisode(medplum, {
            episodeId,
            primaryPatientId: episodePrimaryPatientId,
            guarantorPatientId: values.guarantorPatientId!,
            relationship,
          });
          notifications.show({
            title: 'Guarantor updated',
            message: 'Existing user assigned as guarantor with billing access.',
            color: 'green',
          });
        } else {
          if (!canProvisionPortalUsers) {
            notifications.show({
              title: 'Permission required',
              message: 'Creating a new guarantor portal account requires Manage users permission.',
              color: 'red',
            });
            return;
          }
          await addGuarantorContactToEpisode(medplum, {
            episodeId,
            primaryPatientId: episodePrimaryPatientId,
            contactName: values.contactName.trim(),
            email: values.email.trim(),
            relationship,
            phone: values.phone.trim() || undefined,
          });
          notifications.show({
            title: 'Guarantor updated',
            message: 'Guarantor contact saved. An invite was sent if this is a new portal account.',
            color: 'green',
          });
        }
        resetGuarantorForm();
        refetchChart();
      } else if (guarantorKey && guarantorKey !== ADD_GUARANTOR_KEY) {
        const member = members.find((m) => m.id === guarantorKey);
        let guarantorParty: EpisodeParty | undefined;
        if (member) {
          guarantorParty = { id: guarantorKey, name: member.name, relationship: member.relationship ?? 'Self' };
        } else if (localGuarantor && localGuarantor.id === guarantorKey) {
          guarantorParty = localGuarantor;
        }
        if (!guarantorParty) {
          notifications.show({ title: 'Invalid guarantor', message: 'Select an episode member or add a guarantor below.', color: 'red' });
          return;
        }
        const needsProvision = guarantorParty.id !== episodePrimaryPatientId
          && !guarantorParty.id.startsWith('ext-');
        if (needsProvision && !canProvisionPortalUsers) {
          notifications.show({
            title: 'Permission required',
            message: 'Assigning guarantor portal access requires Manage users permission.',
            color: 'red',
          });
          return;
        }
        await setEpisodeGuarantor(medplum, {
          episodeId,
          primaryPatientId: episodePrimaryPatientId,
          guarantor: guarantorParty,
          relationship: guarantorParty.relationship,
        });
        refetchChart();
        notifications.show({ title: 'Guarantor updated', message: guarantorParty.name, color: 'green' });
      }
    } catch (error) {
      notifications.show({
        title: 'Could not update guarantor',
        message: error instanceof Error ? error.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setPartySaving(false);
    }
  }, [
    clientId, episodeId, baseEpisode, partySaving, addingGuarantor, guarantorAddForm, canProvisionPortalUsers,
    medplum, episodePrimaryPatientId,
    guarantorKey, members, localGuarantor, refetchChart,
    requiresCardOnFile, guarantorBillingLoading, billingPatientId, guarantorHasCard, guarantorHasInsurance,
  ]);

  // ── Guarantor selection change ─────────────────────────────────────
  const handleGuarantorChange = useCallback((value: string | null) => {
    if (value === ADD_GUARANTOR_KEY) {
      guarantorAddForm.reset();
      setAddingGuarantor(true);
      setGuarantorKey(ADD_GUARANTOR_KEY);
      return;
    }
    setAddingGuarantor(false);
    setGuarantorKey(value);
  }, [guarantorAddForm]);

  // ── Add member ──────────────────────────────────────────────────────
  const availableClients = useMemo(() =>
    buildClientSelectOptions(
      allClients
        .filter((c) => !members.some((m) => m.id === c.id))
        .map((c) => ({
          id: c.id,
          name: getClientDisplayName(c),
          email: c.email,
        })),
    ),
    [members, allClients],
  );

  const availableGuarantorClients = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.id));
    return buildClientSelectOptions(
      allClients
        .filter((c) => !memberIds.has(c.id))
        .map((c) => ({
          id: c.id,
          name: getClientDisplayName(c),
          email: c.email,
        })),
    );
  }, [members, allClients]);

  const resetGuarantorForm = () => {
    setAddingGuarantor(false);
    guarantorAddForm.reset();
    setGuarantorKey(members[0]?.id ?? null);
  };

  const handleAddMember = useCallback(async () => {
    if (!episodeId || partySaving) return;

    const validation = memberForm.validate();
    if (validation.hasErrors) return;

    const values = addEpisodeMemberFormSchema.parse(memberForm.values);
    const relationship = episodePartyRelationshipForBot(values.relationship);

    setPartySaving(true);
    try {
      if (values.mode === 'existing') {
        const selectedClient = allClients.find((c) => c.id === values.participantPatientId);
        if (!selectedClient) return;
        await addExistingClientToEpisode(medplum, {
          episodeId,
          primaryPatientId: episodePrimaryPatientId,
          participantPatientId: selectedClient.id,
          relationship,
        });
        notifications.show({ title: 'Member added', message: `${selectedClient.name} added to episode.`, color: 'green' });
      } else {
        if (!canProvisionPortalUsers) {
          notifications.show({
            title: 'Permission required',
            message: 'Creating a new client portal account requires Manage users permission.',
            color: 'red',
          });
          return;
        }
        await addNewClientToEpisode(medplum, {
          episodeId,
          primaryPatientId: episodePrimaryPatientId,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          relationship,
        });
        notifications.show({
          title: 'Client added',
          message: `${values.firstName.trim()} ${values.lastName.trim()} was invited to the episode.`,
          color: 'green',
        });
      }
      resetNewMemberForm();
      refetchChart();
    } catch (error) {
      notifications.show({
        title: 'Could not add member',
        message: error instanceof Error ? error.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setPartySaving(false);
    }
  }, [
    episodeId, partySaving, memberForm, allClients, medplum, episodePrimaryPatientId,
    canProvisionPortalUsers, refetchChart,
  ]);

  // ── Remove member ───────────────────────────────────────────────────
  const cancelRemoval = () => {
    setRemovingMemberId(null);
    setDischargeReason('');
    setDischargeCategory(null);
  };

  const handleConfirmRemove = useCallback(async () => {
    if (!removingMemberId || !dischargeReason.trim() || !episodeId || partySaving) return;

    const removedMember = members.find((m) => m.id === removingMemberId);
    if (!removedMember) return;

    if (removedMember.id === episodePrimaryPatientId) {
      notifications.show({
        title: 'Cannot remove primary client',
        message: 'Reassign the primary clinical patient before removing this member.',
        color: 'red',
      });
      return;
    }

    setPartySaving(true);
    try {
      await removeParticipantFromEpisode(medplum, {
        episodeId,
        primaryPatientId: episodePrimaryPatientId,
        member: removedMember,
      });
      const removedName = removedMember.name;
      if (primaryId === removingMemberId) {
        const remaining = members.filter((m) => m.id !== removingMemberId);
        setPrimaryId(remaining[0]?.id ?? null);
      }
      if (guarantorKey === removingMemberId) {
        const remaining = members.filter((m) => m.id !== removingMemberId);
        setGuarantorKey(remaining[0]?.id ?? null);
      }
      cancelRemoval();
      refetchChart();
      notifications.show({ title: 'Member removed', message: `${removedName} discharged from episode.`, color: 'green' });
    } catch (error) {
      notifications.show({
        title: 'Could not remove member',
        message: error instanceof Error ? error.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setPartySaving(false);
    }
  }, [
    removingMemberId, dischargeReason, episodeId, partySaving, members, medplum, episodePrimaryPatientId,
    primaryId, guarantorKey, refetchChart,
  ]);

  // ── Add contact helpers ─────────────────────────────────────────────
  const cancelAddContact = resetGuarantorForm;

  // ── Loading / error states ──────────────────────────────────────────
  if (!clientId || !episodeId) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          Missing client or episode in URL
        </Alert>
      </Container>
    );
  }
  if ((medplumChartLoading && !medplumChart) || waitForPractitioner) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading episode data…</Text>
        </Stack>
      </Container>
    );
  }
  if (!client) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          Client not found
        </Alert>
      </Container>
    );
  }
  if (!baseEpisode) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Episode not found" color="red" variant="light">
          No episode of care with this id exists for this client.
        </Alert>
      </Container>
    );
  }
  if (!canViewThisEpisode) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconShield size={20} />} title="Access denied" color="red" variant="light">
          You are not a participant on this episode of care. Chart content for other episodes may still be visible
          from the client chart when you have access through your own episode.
        </Alert>
      </Container>
    );
  }

  // ── Computed values ─────────────────────────────────────────────────
  const medicalRecordUrl = `/dashboard/clients/${clientId}?tab=chart`;
  const isRemoving = removingMemberId !== null;

  const guarantorOptions: { group: string; items: { value: string; label: string }[] }[] = (() => {
    const opts: { group: string; items: { value: string; label: string }[] }[] = [];
    const currentIsExternalContact = localGuarantor && !members.some((m) => m.id === localGuarantor.id);
    if (currentIsExternalContact && guarantorKey && guarantorKey !== ADD_GUARANTOR_KEY) {
      opts.push({
        group: 'Current Contact',
        items: [{ value: localGuarantor!.id, label: `${localGuarantor!.name}${localGuarantor!.relationship && localGuarantor!.relationship !== 'Self' ? ` (${localGuarantor!.relationship})` : ''}` }],
      });
    }
    if (members.length > 0) {
      opts.push({
        group: isMultiMemberEpisode ? 'Episode members' : 'Client',
        items: members.map((m) => ({
          value: m.id,
          label: m.relationship && m.relationship !== 'Self'
            ? `${m.name} (${m.relationship})`
            : m.name,
        })),
      });
    }
    opts.push({ group: 'Other', items: [{ value: ADD_GUARANTOR_KEY, label: '+ Add guarantor…' }] });
    return opts;
  })();

  const displayGuarantorKey = addingGuarantor ? null : guarantorKey;

  const saveGuarantorDisabled = addingGuarantor
    ? (guarantorAddForm.values.mode === 'existing'
        ? !guarantorAddForm.values.guarantorPatientId
          || guarantorBillingLoading
          || (requiresCardOnFile && !guarantorHasCard)
        : !guarantorAddForm.values.contactName.trim()
          || !guarantorAddForm.values.email.trim())
    : !guarantorKey
      || guarantorKey === ADD_GUARANTOR_KEY
      || guarantorBillingLoading
      || (requiresCardOnFile && !guarantorHasCard);

  const canConfirmPrimaryChange = clinicalPatientChanging && switchReason.trim();

  // ── Render: member card ─────────────────────────────────────────────
  const renderMemberCard = (member: EpisodeParty) => {
    const isBeingRemoved = removingMemberId === member.id;
    const isPrimary = member.id === primaryId;
    const isBlockedPrimary = isMultiMemberEpisode && isPrimary && members.length > 1;
    const removeDisabled = isRemoving || addingMember || isBlockedPrimary;
    const removeTooltip = isBlockedPrimary
      ? 'Reassign the primary patient first'
      : 'Remove from episode';

    return (
      <Box key={member.id}>
        <Paper
          withBorder
          p="sm"
          style={{
            borderColor: isBeingRemoved ? 'var(--mantine-color-red-4)' : undefined,
            backgroundColor: isBeingRemoved ? 'var(--mantine-color-red-0)' : undefined,
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              {isMultiMemberEpisode && (
                <Radio
                  value={member.id}
                  disabled={isRemoving}
                  aria-label={`Set ${member.name} as primary clinical patient`}
                />
              )}
              <div style={{ minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text
                    component={Link}
                    to={`/dashboard/clients/${member.id}`}
                    size="sm"
                    fw={500}
                    truncate
                    c="gold.7"
                    style={{ textDecoration: 'none' }}
                  >
                    {member.name}
                  </Text>
                  {isPrimary && (
                    <Badge size="xs" color="blue" variant="light">Primary</Badge>
                  )}
                </Group>
                {member.relationship && member.relationship !== 'Self' && (
                  <Text size="xs" c="gray.6">{member.relationship}</Text>
                )}
              </div>
            </Group>

            {isMultiMemberEpisode && member.id !== episodePrimaryPatientId && (
              isBeingRemoved ? (
                <Tooltip label="Cancel removal" withArrow>
                  <ActionIcon size="sm" variant="subtle" color="gray" onClick={cancelRemoval} aria-label="Cancel removal">
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              ) : (
                <Tooltip label={removeTooltip} withArrow>
                  <Box component="span" style={{ display: 'inline-block' }}>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color={isBlockedPrimary ? 'gray' : 'red'}
                      disabled={removeDisabled || partySaving}
                      style={removeDisabled || partySaving ? { pointerEvents: 'none' } : undefined}
                      onClick={() => {
                        setRemovingMemberId(member.id);
                        setDischargeReason('');
                        setDischargeCategory(null);
                      }}
                      aria-label={`Remove ${member.name}`}
                    >
                      <IconUserMinus size={14} />
                    </ActionIcon>
                  </Box>
                </Tooltip>
              )
            )}
          </Group>
        </Paper>

        {isBeingRemoved && (
          <Paper
            p="md"
            style={{
              border: '1px solid var(--mantine-color-red-3)',
              borderTop: 'none',
              borderRadius: '0 0 var(--mantine-radius-sm) var(--mantine-radius-sm)',
              backgroundColor: 'var(--mantine-color-red-0)',
            }}
          >
            <Stack gap="sm">
              <Alert icon={<IconAlertTriangleFilled size={14} />} color="red" variant="light" title={`Removing ${member.name}`} py="xs">
                A discharge summary is required before removing a member.
              </Alert>
              <Select
                label="Reason for discharge"
                placeholder="Select a reason..."
                data={DISCHARGE_REASONS}
                value={dischargeCategory}
                onChange={setDischargeCategory}
                size="sm"
              />
              <Textarea
                label="Discharge summary"
                placeholder="Summarize the course of treatment, progress made, and follow-up recommendations..."
                value={dischargeReason}
                onChange={(e) => setDischargeReason(e.currentTarget.value)}
                minRows={4}
                autosize
                maxRows={10}
                required
                size="sm"
              />
              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="default" onClick={cancelRemoval}>Cancel</Button>
                <Button size="xs" color="red" leftSection={<IconUserMinus size={14} />} disabled={!dischargeReason.trim()} onClick={handleConfirmRemove}>
                  Confirm Removal
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      </Box>
    );
  };

  // ── Breadcrumbs ─────────────────────────────────────────────────────
  const breadcrumbItems = [
    { title: 'Caseload', href: '/dashboard/caseload' },
    { title: client.name, href: `/dashboard/clients/${clientId}` },
    { title: 'Chart', href: medicalRecordUrl },
    { title: 'Episode of Care', href: '' },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* ── Breadcrumbs ────────────────────────────────────────── */}
        <Group gap="xs" align="center">
          <ActionIcon variant="subtle" color="gold.7" size="lg" onClick={() => navigate(medicalRecordUrl)} aria-label="Go back">
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Breadcrumbs>
            {breadcrumbItems.map((item, index) =>
              item.href ? (
                <Text key={index} component={Link} to={item.href} c="gold.7" size="sm" fw={500}>
                  {item.title}
                </Text>
              ) : (
                <Text key={index} size="sm" c="gray.6">{item.title}</Text>
              ),
            )}
          </Breadcrumbs>
        </Group>

        {/* ── Page Header ────────────────────────────────────────── */}
        <RecordPageHeader
          recordType="Episode of Care"
          recordTypeColor="purple"
          icon={<IconClipboardList size={12} style={{ color: 'var(--mantine-color-violet-7)' }} />}
          secondaryTitle={
            <Text component={Link} to={`/dashboard/clients/${clientId}?tab=chart&episodeId=${baseEpisode.id}`} c="gold.7" inherit style={{ textDecoration: 'none' }}>
              {baseEpisode.name}
            </Text>
          }
          secondaryRow={
            <Stack gap={2}>
              <Text size="sm" fw={500} c="gray.7" component="span" style={{ lineHeight: 1.3 }}>
                {baseEpisode.provider} • Started {formatDate(baseEpisode.startDate)}
                {baseEpisode.endDate ? ` – Ended ${formatDate(baseEpisode.endDate)}` : ''}
              </Text>
            </Stack>
          }
          rightSide={
            <Group gap="xs">
              <Badge color={isPrimaryChart ? 'blue' : 'gray'} variant="light" size="sm">
                {isPrimaryChart ? 'Primary' : 'Supporting'}
              </Badge>
              <Badge color={getStatusColor(baseEpisode.status)} variant="light" size="sm">
                {formatStatusText(baseEpisode.status)}
              </Badge>
              <Button
                variant="light"
                size="xs"
                leftSection={<IconTransfer size={14} />}
                onClick={() => setTransferModalOpen(true)}
              >
                Transfer
              </Button>
            </Group>
          }
          clientHref={medicalRecordUrl}
          clientPreferredName={getClientDisplayName(client)}
          clientLegalName={(() => {
            const legal = getClientLegalName(client);
            const preferred = getClientDisplayName(client);
            return legal && legal !== preferred ? legal : undefined;
          })()}
          clientPronouns={client.pronouns}
          clientDob={client.dateOfBirth ? formatDate(client.dateOfBirth) : undefined}
          clientPhone={client.phone}
          clientEmail={client.email}
        />

        {/* ── Episode Details ─────────────────────────────────────── */}
        <Paper p="lg" withBorder>
          <Group gap="xs" mb="sm">
            <IconClipboardList size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
            <Title order={5}>Episode Details</Title>
          </Group>
          <Group gap="lg" wrap="wrap" align="flex-start">
            <Stack gap={4}>
              <Text size="xs" c="gray.5" fw={600} tt="uppercase">Type</Text>
              <Box style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
                <Text size="sm">{episodeTypeLabel ?? (isMultiMemberEpisode ? 'Group' : 'Individual')}</Text>
              </Box>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="gray.5" fw={600} tt="uppercase">Status</Text>
              <Box style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
                <Badge color={getStatusColor(baseEpisode.status)} variant="light" size="sm">
                  {formatStatusText(baseEpisode.status)}
                </Badge>
              </Box>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="gray.5" fw={600} tt="uppercase">Provider</Text>
              <Text size="sm">{baseEpisode.provider}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="gray.5" fw={600} tt="uppercase">Diagnosis</Text>
              {episodeDiagnosisLoading ? (
                <Loader size="xs" />
              ) : (
                <Text size="sm" c={episodeDiagnosis ? undefined : 'gray.5'}>
                  {episodeDiagnosis ?? 'Not recorded'}
                </Text>
              )}
            </Stack>
            {baseEpisode.primaryClinicalPatient && (
              <Stack gap={4}>
                <Text size="xs" c="gray.5" fw={600} tt="uppercase">Primary Client</Text>
                <Box style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
                  <Text
                    component={Link}
                    to={`/dashboard/clients/${baseEpisode.primaryClinicalPatient.id}`}
                    size="sm"
                    fw={500}
                    c="gold.7"
                    style={{ textDecoration: 'none' }}
                  >
                    {baseEpisode.primaryClinicalPatient.name}
                  </Text>
                </Box>
              </Stack>
            )}
            {localGuarantor && (
              <Stack gap={4}>
                <Text size="xs" c="gray.5" fw={600} tt="uppercase">Guarantor</Text>
                <Box style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
                  <Text
                    component={Link}
                    to={`/dashboard/clients/${primaryClientForGuarantorLink}/profile?tab=billing`}
                    size="sm"
                    fw={500}
                    c="gold.7"
                    style={{ textDecoration: 'none' }}
                  >
                    {localGuarantor.name}
                    {localGuarantor.relationship && localGuarantor.relationship !== 'Self' && (
                      <Text span c="gray.5" size="xs"> ({localGuarantor.relationship})</Text>
                    )}
                  </Text>
                </Box>
              </Stack>
            )}
          </Group>
        </Paper>

        {/* ── Members Section ────────────────────────────────────── */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="xs">
                <IconUsers size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
                <Title order={5}>{isMultiMemberEpisode ? 'Group Members' : 'Client'}</Title>
                {episodeTypeLabel && (
                  <Badge variant="light" color="blue" size="sm">{episodeTypeLabel}</Badge>
                )}
              </Group>
              {isMultiMemberEpisode && !addingMember && (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconUserPlus size={14} />}
                  onClick={() => {
                    memberForm.reset();
                    setAddingMember(true);
                  }}
                  disabled={isRemoving || partySaving}
                >
                  Add member
                </Button>
              )}
            </Group>

            {isMultiMemberEpisode && (
              <Text size="xs" c="gray.6">
                Use the radio buttons to set the primary clinical patient. Remove a member to discharge them from this episode.
              </Text>
            )}

            {members.length === 0 ? (
              <Alert icon={<IconUsers size={14} />} color="gray" variant="light" title="No active members">
                All members have been discharged from this episode.
              </Alert>
            ) : isMultiMemberEpisode ? (
              <Radio.Group value={primaryId ?? ''} onChange={setPrimaryId} name="primary-clinical-patient">
                <Stack gap="sm">{members.map(renderMemberCard)}</Stack>
              </Radio.Group>
            ) : (
              <Stack gap="sm">{members.map(renderMemberCard)}</Stack>
            )}

            {/* Add member inline form */}
            {addingMember && (
              <Paper withBorder p="md" style={{ borderColor: 'var(--mantine-color-blue-3)', backgroundColor: 'var(--mantine-color-blue-0)' }}>
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconUserPlus size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
                    <Text size="sm" fw={600} c="blue.7">Add Member</Text>
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={resetNewMemberForm} ml="auto" aria-label="Cancel">
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                  <SegmentedControl
                    value={memberForm.values.mode}
                    onChange={(value) => memberForm.setFieldValue('mode', value as 'existing' | 'new')}
                    data={[
                      { label: 'Existing user', value: 'existing' },
                      { label: 'New client', value: 'new', disabled: !canProvisionPortalUsers },
                    ]}
                    size="xs"
                  />
                  {!canProvisionPortalUsers && memberForm.values.mode === 'new' && (
                    <Text size="xs" c="dimmed">Creating new portal accounts requires Manage users permission.</Text>
                  )}
                  {memberForm.values.mode === 'existing' ? (
                    <Select
                      label="Select client"
                      placeholder="Search clients..."
                      data={availableClients}
                      value={memberForm.values.participantPatientId}
                      onChange={(value) => memberForm.setFieldValue('participantPatientId', value)}
                      error={memberForm.errors.participantPatientId}
                      searchable
                      size="sm"
                      renderOption={({ option }) => (
                        <ClientSelectOptionLabel label={option.label} />
                      )}
                    />
                  ) : (
                    <Group grow gap="xs">
                      <TextInput label="First name" required size="sm" {...memberForm.getInputProps('firstName')} />
                      <TextInput label="Last name" required size="sm" {...memberForm.getInputProps('lastName')} />
                      <TextInput label="Email" required size="sm" {...memberForm.getInputProps('email')} />
                    </Group>
                  )}
                  <Select
                    label="Relationship to primary client"
                    description="Optional — leave blank when none of the options apply"
                    placeholder="Select if known (optional)"
                    data={episodePartyRelationshipOptions}
                    value={memberForm.values.relationship || null}
                    onChange={(value) => memberForm.setFieldValue('relationship', (value ?? '') as AddEpisodeMemberFormValues['relationship'])}
                    error={memberForm.errors.relationship}
                    clearable
                    size="sm"
                  />
                  <Group justify="flex-end" gap="xs">
                    <Button size="xs" variant="default" onClick={resetNewMemberForm}>Cancel</Button>
                    <Button
                      size="xs"
                      leftSection={<IconUserPlus size={14} />}
                      loading={partySaving}
                      onClick={handleAddMember}
                    >
                      Add Member
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>

        {/* ── Primary Patient Change (inline in members flow) ──── */}
        {clinicalPatientChanging && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" title="Primary patient change pending">
            <Stack gap="sm" mt="xs">
              <Text size="sm">
                Update the <strong>diagnosis</strong> and <strong>treatment plan</strong> below to reflect the new primary patient.
                This change creates a permanent audit record.
              </Text>
              <Textarea
                label="Reason for change"
                placeholder="e.g. Insurance change — previous member's coverage lapsed"
                value={switchReason}
                onChange={(e) => setSwitchReason(e.currentTarget.value)}
                required
                minRows={2}
                size="sm"
              />
              <Group justify="flex-end">
                <Button
                  size="sm"
                  color="orange"
                  disabled={!canConfirmPrimaryChange}
                  onClick={handleConfirmPrimaryChange}
                >
                  Confirm Primary Change
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/* ── Guarantor & Billing ────────────────────────────────── */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Group gap="xs">
              <IconShield size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
              <Title order={5}>Guarantor & Billing</Title>
            </Group>

            <Alert variant="light" color="blue" icon={<IconCreditCard size={16} />} py="xs">
              <Text size="sm">
                Updating billing for <strong>{baseEpisode.primaryClinicalPatient?.name ?? getClientDisplayName(client)}</strong> on this Episode of Care.
              </Text>
            </Alert>

            <Text size="xs" c="gray.6">
              The guarantor is the person whose insurance covers this episode or who is financially responsible for any balance.
              Insurance and card details are read from the guarantor&apos;s client billing profile.
            </Text>

            <Group gap="sm" align="flex-end">
              <Select
                label="Guarantor"
                data={guarantorOptions}
                value={displayGuarantorKey}
                onChange={handleGuarantorChange}
                leftSection={<IconShield size={14} />}
                disabled={isRemoving || partySaving}
                placeholder="Select guarantor..."
                clearable={false}
                style={{ flex: 1 }}
              />
              <Button
                variant="light"
                color="gold"
                loading={partySaving}
                onClick={handleSaveGuarantor}
                disabled={saveGuarantorDisabled}
              >
                Update Guarantor
              </Button>
            </Group>

            {addingGuarantor && (
              <Paper withBorder p="md" style={{ borderColor: 'var(--mantine-color-blue-3)', backgroundColor: 'var(--mantine-color-blue-0)' }}>
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconUserPlus size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
                    <Text size="sm" fw={600} c="blue.7">Add Guarantor</Text>
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={cancelAddContact} ml="auto" aria-label="Cancel">
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                  <Text size="xs" c="gray.6">
                    Add someone who is not yet on this episode. Existing practice clients must have a card on file before they can be assigned as guarantor.
                  </Text>
                  <SegmentedControl
                    value={guarantorAddForm.values.mode}
                    onChange={(value) => guarantorAddForm.setFieldValue('mode', value as 'existing' | 'new')}
                    data={[
                      { label: 'Existing user', value: 'existing' },
                      { label: 'New contact', value: 'new', disabled: !canProvisionPortalUsers },
                    ]}
                    size="xs"
                  />
                  {guarantorAddForm.values.mode === 'existing' ? (
                    <>
                      <Select
                        label="Select user"
                        placeholder="Search practice clients..."
                        data={availableGuarantorClients}
                        value={guarantorAddForm.values.guarantorPatientId}
                        onChange={(value) => guarantorAddForm.setFieldValue('guarantorPatientId', value)}
                        error={guarantorAddForm.errors.guarantorPatientId}
                        searchable
                        size="sm"
                      />
                      <Select
                        label="Relationship to primary client"
                        description="Optional — leave blank when none of the options apply"
                        placeholder="Select if known (optional)"
                        data={episodePartyRelationshipOptions}
                        value={guarantorAddForm.values.relationship || null}
                        onChange={(value) => guarantorAddForm.setFieldValue('relationship', (value ?? '') as AddEpisodeGuarantorFormValues['relationship'])}
                        error={guarantorAddForm.errors.relationship}
                        clearable
                        size="sm"
                      />
                    </>
                  ) : (
                    <>
                      <Group grow gap="xs">
                        <TextInput
                          label="Full name"
                          placeholder="e.g. Robert Rivera"
                          required
                          size="sm"
                          {...guarantorAddForm.getInputProps('contactName')}
                        />
                        <Select
                          label="Relationship to patient"
                          description="Optional — leave blank when none of the options apply"
                          placeholder="Select if known (optional)"
                          data={episodePartyRelationshipOptions}
                          value={guarantorAddForm.values.relationship || null}
                          onChange={(value) => guarantorAddForm.setFieldValue('relationship', (value ?? '') as AddEpisodeGuarantorFormValues['relationship'])}
                          error={guarantorAddForm.errors.relationship}
                          clearable
                          size="sm"
                        />
                      </Group>
                      <Group grow gap="xs">
                        <TextInput
                          label="Email"
                          placeholder="guarantor@example.com"
                          required
                          size="sm"
                          {...guarantorAddForm.getInputProps('email')}
                        />
                        <TextInput
                          label="Phone"
                          placeholder="(555) 000-0000"
                          size="sm"
                          {...guarantorAddForm.getInputProps('phone')}
                        />
                      </Group>
                    </>
                  )}
                  {!canProvisionPortalUsers && (
                    <Text size="xs" c="dimmed">Assigning guarantor portal access requires Manage users permission.</Text>
                  )}
                </Stack>
              </Paper>
            )}

            {!addingGuarantor && requiresCardOnFile && !guarantorBillingLoading && billingPatientId && !guarantorHasCard && (
              <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />} py="xs">
                <Text size="sm">
                  This guarantor does not have a card on file. Add a card on their{' '}
                  <Text
                    component={Link}
                    to={`/dashboard/clients/${billingPatientId}/profile?tab=billing`}
                    span
                    c="gold.7"
                    fw={500}
                  >
                    billing profile
                  </Text>{' '}
                  before updating the episode guarantor.
                </Text>
              </Alert>
            )}

            {!addingGuarantor && requiresCardOnFile && !guarantorBillingLoading && billingPatientId && guarantorHasCard && !guarantorHasInsurance && (
              <Alert variant="light" color="yellow" icon={<IconAlertTriangleFilled size={16} />} py="xs">
                <Text size="sm">
                  This guarantor has no insurance on file. You can still save, but verify coverage before billing sessions.
                </Text>
              </Alert>
            )}

            <Divider label="Insurance on file" labelPosition="left" />
            {!billingPatientId ? (
              <Text size="sm" c="gray.6">
                Select an episode member to view their insurance details.
              </Text>
            ) : guarantorBillingLoading ? (
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="gray.6">Loading insurance…</Text>
              </Group>
            ) : guarantorInsuranceDisplay ? (
              <Group grow gap="sm">
                <TextInput label="Insurance payer" value={guarantorInsuranceDisplay.payer} readOnly size="sm" />
                <TextInput label="Member ID" value={guarantorInsuranceDisplay.memberId || '—'} readOnly size="sm" />
                <TextInput label="Group ID" value={guarantorInsuranceDisplay.groupId || '—'} readOnly size="sm" />
              </Group>
            ) : (
              <Stack gap="xs">
                <Text size="sm" c="gray.6">No insurance on file for this guarantor.</Text>
                <Button
                  component={Link}
                  to={`/dashboard/clients/${billingPatientId}/profile?tab=billing`}
                  variant="light"
                  size="xs"
                  w="fit-content"
                >
                  Manage on billing profile
                </Button>
              </Stack>
            )}

            <Divider label="Card on file" labelPosition="left" />
            {!billingPatientId ? (
              <Text size="sm" c="gray.6">
                Select an episode member to view their card on file.
              </Text>
            ) : (
              <CardOnFileSection
                paymentMethods={guarantorPaymentMethods}
                loading={guarantorBillingLoading}
              />
            )}
          </Stack>
        </Paper>

        {dischargeSummary && (
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Title order={5}>Discharge summary</Title>
              <Text size="xs" c="gray.6">
                {formatDate(dischargeSummary.dischargeDate)}
                {dischargeSummary.signedBy ? ` • Signed by ${dischargeSummary.signedBy}` : ''}
              </Text>
              <Stack gap="sm">
                <div>
                  <Text size="xs" c="gray.6" fw={500}>Reason for discharge</Text>
                  <Text size="sm" c="gray.8">{dischargeSummary.reasonForDischarge}</Text>
                </div>
                <div>
                  <Text size="xs" c="gray.6" fw={500}>Summary of care</Text>
                  <Text size="sm" c="gray.8">{dischargeSummary.summaryOfCare}</Text>
                </div>
                <div>
                  <Text size="xs" c="gray.6" fw={500}>Recommendations</Text>
                  <Text size="sm" c="gray.8">{dischargeSummary.recommendations}</Text>
                </div>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* ── Treatment plans ─────────────────────────────────────── */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Group gap="xs">
              <IconClipboardList size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
              <Title order={5}>Treatment plans</Title>
            </Group>
            {treatmentPlanVersionsLoading ? (
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Loading treatment plans…</Text>
              </Group>
            ) : treatmentPlanVersions.length === 0 ? (
              <Text size="sm" c="dimmed">
                No treatment plans documented for this episode yet.
              </Text>
            ) : (
              <ScrollArea.Autosize mah={280} type="scroll" offsetScrollbars>
                <Stack gap="sm">
                  {treatmentPlanVersions.map((version) => (
                    <Group key={version.key} justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {version.title}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {version.updatedAt ? formatDate(version.updatedAt) : '—'}
                          {` · ${version.status}`}
                          {version.versionId ? ` · v${version.versionId}` : ''}
                        </Text>
                      </Stack>
                      {version.chartDocumentId && clientId ? (
                        <Anchor
                          component={Link}
                          to={
                            version.isCurrent || !version.versionId
                              ? `/dashboard/clients/${clientId}/chart-document/${version.chartDocumentId}`
                              : `/dashboard/clients/${clientId}/chart-document/${version.chartDocumentId}?carePlanVersionId=${encodeURIComponent(version.versionId)}`
                          }
                          size="sm"
                          fw={500}
                          style={{ flexShrink: 0 }}
                        >
                          Open
                        </Anchor>
                      ) : null}
                    </Group>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Stack>
        </Paper>

        {/* ── Change History ──────────────────────────────────────── */}
        {localHistory.length > 0 && (
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <IconHistory size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
                <Title order={5}>Change History</Title>
              </Group>
              <Stack gap="xs">
                {localHistory.map((entry, i) => (
                  <Box
                    key={i}
                    p="sm"
                    style={{
                      background: 'var(--mantine-color-gray-0)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      borderLeft: '3px solid var(--mantine-color-orange-4)',
                    }}
                  >
                    <Text size="sm" fw={500} c="gray.8">
                      {entry.previousPatient.name} → {entry.newPatient.name}
                    </Text>
                    <Text size="xs" c="gray.6">
                      {formatLocalDate(entry.changedAt)} · Changed by {entry.changedBy}
                    </Text>
                    <Text size="xs" c="gray.6" mt={2}>Reason: {entry.reason}</Text>
                    {entry.treatmentPlanUpdated && (
                      <Badge size="xs" color="green" variant="light" mt={4}>Treatment plan updated</Badge>
                    )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>

      <TransferModal
        opened={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        context={
          clientId && episodeId
            ? ({
                patientId: clientId,
                patientDisplay: client ? getClientDisplayName(client) : undefined,
                episodeOfCareId: episodeId,
                episodeDisplay: baseEpisode?.name,
                serviceRequestId: '',
                currentProviderDisplay: baseEpisode?.provider ?? client?.primaryProvider ?? client?.provider,
                serviceTypeDisplay: episodeTypeLabel,
                episodeOptions: (medplumChart?.episodes ?? []).map((ep) => ({
                  value: ep.id,
                  label: ep.name,
                  currentProviderDisplay: ep.provider ?? client?.primaryProvider ?? client?.provider,
                  serviceTypeDisplay: getEpisodeTypeLabel(ep.episodeType) ?? client?.clientType,
                })),
              } as TransferModalContext)
            : null
        }
        onSuccess={() => setTransferModalOpen(false)}
      />
    </Container>
  );
}
